import {
  type JsonSchema,
  type JsonSchemaObject,
  type JsonSchemaPrimitive,
  type JsonType,
  type JsonValue,
  type SchemaTypeValue,
} from '@/domain/entities/property-schema';

import type { AttributeNode, HvMeta } from '@/types/schemas';

export function nowUnixSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

export function uid(prefix = 'id'): string {
  // crypto.randomUUID is not available in all environments; keep safe fallback

  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

export function withNullable(type: JsonType, nullable?: boolean): SchemaTypeValue {
  return nullable ? [type, 'null'] : type;
}

export const JSON_TYPE_OPTIONS: { value: JsonType; label: string }[] = [
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'boolean', label: 'boolean' },
  { value: 'object', label: 'object' },
  { value: 'array', label: 'array' },
];

export function isValidJsonLike(value: string): boolean {
  if (!value.trim()) return true;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

export function getHvMeta(schema: JsonSchema): HvMeta | undefined {
  const obj = schema as unknown as Record<string, unknown>;
  const hv = obj['x-hv'];

  if (!hv || typeof hv !== 'object' || Array.isArray(hv)) return undefined;

  const out: HvMeta = {};
  for (const [k, v] of Object.entries(hv as Record<string, unknown>)) {
    out[k] = normalizeIncomingXhvValue(k, v);
  }
  return Object.keys(out).length ? out : undefined;
}

export function buildAttributeSchema(node: AttributeNode): JsonSchema {
  const normalizedHv = normalizeHvMeta(node.x);
  const hv = normalizedHv ? { 'x-hv': normalizedHv } : {};

  if (node.type === 'array') {
    return {
      ...hv,
      type: withNullable('array', node.nullable),
      items: node.items ? buildAttributeSchema(node.items) : { type: 'string' },
    };
  }

  if (node.type === 'object') {
    const required = (node.attributes ?? []).filter((a) => a.required && a.key.trim()).map((a) => a.key.trim());

    const attributes: Record<string, JsonSchema> = {};
    for (const a of node.attributes ?? []) {
      const k = a.key.trim();
      if (!k) continue;
      attributes[k] = buildAttributeSchema(a);
    }

    const schema: JsonSchemaObject = {
      ...hv,
      type: withNullable('object', node.nullable),
      attributes,
    };
    if (required.length) schema.required = required;
    return schema;
  }

  const base: JsonSchemaPrimitive = { ...hv, type: withNullable(node.type, node.nullable) };
  if (node.type === 'string' && node.enum && node.enum.length) base.enum = node.enum;
  return base;
}

export function buildRootSchema(rootBlocks: AttributeNode[]): JsonSchemaObject {
  const required = rootBlocks.filter((b) => b.required && b.key.trim()).map((b) => b.key.trim());

  const attributes: Record<string, JsonSchema> = {};
  for (const b of rootBlocks) {
    const k = b.key.trim();
    if (!k) continue;
    attributes[k] = buildAttributeSchema(b);
  }

  const schema: JsonSchemaObject = { type: 'object', attributes };
  if (required.length) schema.required = required;
  return schema;
}

export function dedupeAndValidateKeys(nodes: AttributeNode[]): { ok: boolean; error?: string } {
  const keys = nodes.map((n) => n.key.trim()).filter(Boolean);
  const dup = keys.find((k, idx) => keys.indexOf(k) !== idx);
  if (dup) return { ok: false, error: `Duplicate key: "${dup}"` };
  return { ok: true };
}

export function validateTree(node: AttributeNode, path: string): { ok: boolean; error?: string } {
  const key = node.key.trim();
  if (!key) return { ok: false, error: `Missing key at ${path}` };

  if (node.type === 'object') {
    const children = node.attributes ?? [];
    const res = dedupeAndValidateKeys(children);
    if (!res.ok) return { ok: false, error: `${res.error} under ${path}.${key}` };
    for (const child of children) {
      const childRes = validateTree(child, `${path}.${key}`);
      if (!childRes.ok) return childRes;
    }
  }

  if (node.type === 'array') {
    if (node.items) {
      const itemRes = validateTree(node.items, `${path}.${key}[items]`);
      if (!itemRes.ok) return itemRes;
    }
  }

  return { ok: true };
}

export function normalizeNodeForType(node: AttributeNode, nextType: JsonType): AttributeNode {
  const base: AttributeNode = { ...node, type: nextType };

  if (nextType === 'object') {
    return { ...base, enum: undefined, items: undefined, attributes: base.attributes ?? [] };
  }
  if (nextType === 'array') {
    return {
      ...base,
      enum: undefined,
      attributes: undefined,
      items:
        base.items ??
        ({
          id: uid('attr'),
          key: '__items__',
          type: 'string',
          nullable: false,
          required: false,
        } as AttributeNode),
    };
  }
  return { ...base, attributes: undefined, items: undefined, enum: nextType === 'string' ? base.enum : undefined };
}

export function makeEmptyBlock(): AttributeNode {
  return {
    id: uid('blk'),
    key: '',
    type: 'object',
    required: false,
    nullable: false,
    attributes: [],
  };
}

export function makeEmptyAttribute(): AttributeNode {
  return {
    id: uid('attr'),
    key: '',
    type: 'string',
    nullable: false,
    required: false,
  };
}

export function cloneDeep<T>(x: T): T {
  if (typeof globalThis.structuredClone === 'function') return globalThis.structuredClone(x);
  return JSON.parse(JSON.stringify(x)) as T;
}

function isNullableType(type: SchemaTypeValue): { base: JsonType; nullable: boolean } {
  if (Array.isArray(type)) {
    const nullable = type.includes('null');
    const base = (type.find((t) => t !== 'null') ?? 'string') as JsonType;
    return { base, nullable };
  }
  return { base: type as JsonType, nullable: false };
}

export function schemaToNode(key: string, schema: JsonSchema, requiredKeys: Set<string>): AttributeNode {
  const { base, nullable } = isNullableType(schema.type);
  const x = getHvMeta(schema);

  if (base === 'object') {
    const s = schema as JsonSchemaObject;
    const childRequired = new Set<string>(s.required ?? []);
    const children = Object.entries(s.attributes ?? {}).map(([k, v]) => schemaToNode(k, v, childRequired));
    return {
      id: uid('attr'),
      key,
      type: 'object',
      nullable,
      required: requiredKeys.has(key),
      attributes: children,
      x,
    };
  }

  if (base === 'array') {
    const s = schema as { type: SchemaTypeValue; items: JsonSchema };
    const itemNode = schemaToNode('item', s.items, new Set());
    return {
      id: uid('attr'),
      key,
      type: 'array',
      nullable,
      required: requiredKeys.has(key),
      items: { ...itemNode, key: 'item' },
      x,
    };
  }

  const s = schema as JsonSchemaPrimitive;
  return {
    id: uid('attr'),
    key,
    type: base,
    nullable,
    required: requiredKeys.has(key),
    enum: base === 'string' ? s.enum : undefined,
    x,
  };
}

export function schemaToBlocks(root: JsonSchemaObject): AttributeNode[] {
  const rootRequired = new Set<string>(root.required ?? []);
  return Object.entries(root.attributes ?? {}).map(([k, v]) => schemaToNode(k, v, rootRequired));
}

export function isJsonSchemaObject(schema: JsonSchema | null | undefined): schema is JsonSchemaObject {
  if (!schema || typeof schema !== 'object') return false;

  const t = (schema as JsonSchemaObject).type;
  // root object can be "object" or ["object","null"]
  const isObjType = t === 'object' || (Array.isArray(t) && t.includes('object'));
  if (!isObjType) return false;

  const attrs = (schema as JsonSchemaObject).attributes;
  return !!attrs && typeof attrs === 'object' && !Array.isArray(attrs);
}

const VALID_SCHEMA_TYPES = new Set<JsonType>(['string', 'number', 'boolean', 'object', 'array']);

type SchemaValidationResult = { ok: true; schema: JsonSchemaObject } | { ok: false; errors: string[] };

function formatPath(path: string, child: string): string {
  return path ? `${path}.${child}` : child;
}

function validateSchemaType(
  value: unknown,
  path: string,
): { ok: boolean; base?: JsonType; nullable?: boolean; errors: string[] } {
  if (typeof value === 'string') {
    if (VALID_SCHEMA_TYPES.has(value as JsonType)) {
      return { ok: true, base: value as JsonType, nullable: false, errors: [] };
    }
    return { ok: false, errors: [`${path}.type must be one of string, number, boolean, object, or array.`] };
  }

  if (Array.isArray(value)) {
    const unique = new Set(value);
    const nonNullTypes = value.filter((entry) => entry !== 'null');
    const base = nonNullTypes[0];
    const hasOnlyValidEntries = value.every((entry) => entry === 'null' || VALID_SCHEMA_TYPES.has(entry as JsonType));

    if (
      value.length >= 1 &&
      value.length <= 2 &&
      unique.size === value.length &&
      nonNullTypes.length === 1 &&
      typeof base === 'string' &&
      VALID_SCHEMA_TYPES.has(base as JsonType) &&
      hasOnlyValidEntries
    ) {
      return { ok: true, base: base as JsonType, nullable: value.includes('null'), errors: [] };
    }

    return {
      ok: false,
      errors: [`${path}.type must be a valid type or [type, "null"].`],
    };
  }

  return { ok: false, errors: [`${path}.type is required.`] };
}

function validateXhvMeta(value: unknown, path: string): string[] {
  if (typeof value === 'undefined') return [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [`${path}.x-hv must be an object when provided.`];
  }
  return [];
}

function validateJsonSchemaNode(value: unknown, path: string): string[] {
  const errors: string[] = [];

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [`${path} must be an object.`];
  }

  const schema = value as Record<string, unknown>;
  const typeResult = validateSchemaType(schema.type, path);
  errors.push(...typeResult.errors);
  errors.push(...validateXhvMeta(schema['x-hv'], path));

  if (!typeResult.ok || !typeResult.base) return errors;

  if (typeof schema.required !== 'undefined') {
    if (!Array.isArray(schema.required) || !schema.required.every((entry) => typeof entry === 'string')) {
      errors.push(`${path}.required must be an array of strings when provided.`);
    }
  }

  if (typeResult.base === 'object') {
    const attributes = schema.attributes;
    if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) {
      errors.push(`${path}.attributes must be an object.`);
      return errors;
    }

    const attributeKeys = new Set(Object.keys(attributes));
    const required = Array.isArray(schema.required)
      ? schema.required.filter((entry): entry is string => typeof entry === 'string')
      : [];
    for (const requiredKey of required) {
      if (!attributeKeys.has(requiredKey)) {
        errors.push(`${path}.required contains "${requiredKey}", but ${path}.attributes.${requiredKey} is missing.`);
      }
    }

    for (const [key, child] of Object.entries(attributes)) {
      if (!key.trim()) {
        errors.push(`${path}.attributes contains an empty key.`);
        continue;
      }
      errors.push(...validateJsonSchemaNode(child, formatPath(path, `attributes.${key}`)));
    }
  }

  if (typeResult.base === 'array') {
    if (typeof schema.items === 'undefined') {
      errors.push(`${path}.items is required for array schemas.`);
    } else {
      errors.push(...validateJsonSchemaNode(schema.items, formatPath(path, 'items')));
    }
  }

  if (typeof schema.enum !== 'undefined') {
    if (!Array.isArray(schema.enum) || !schema.enum.every((entry) => typeof entry === 'string')) {
      errors.push(`${path}.enum must be an array of strings when provided.`);
    } else if (typeResult.base !== 'string') {
      errors.push(`${path}.enum is only supported for string schemas.`);
    }
  }

  return errors;
}

export function validateSchemaPayload(payload: unknown): SchemaValidationResult {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ok: false, errors: ['Payload must be a JSON object.'] };
  }

  const maybeRecord = payload as { jsonSchema?: unknown };
  const candidateSchema = typeof maybeRecord.jsonSchema !== 'undefined' ? maybeRecord.jsonSchema : payload;
  const errors = validateJsonSchemaNode(candidateSchema, 'jsonSchema');

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  if (!isJsonSchemaObject(candidateSchema as JsonSchema)) {
    return { ok: false, errors: ['jsonSchema must be a root object schema with an attributes object.'] };
  }

  return { ok: true, schema: candidateSchema as JsonSchemaObject };
}

export function normalizeXhvValue(key: string, raw: string): JsonValue {
  const XHV_ARRAY_KEYS = new Set(['systemTypes', 'excludeSystemTypes'] as const);
  // array keys must become string[] (or undefined)
  if (XHV_ARRAY_KEYS.has(key as 'systemTypes' | 'excludeSystemTypes')) {
    const trimmed = raw.trim();
    if (!trimmed) return []; // or undefined if you prefer removing empty
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) return parsed;
      // allow single string entered -> wrap it
      if (typeof parsed === 'string') return [parsed];
    } catch {
      // allow "cooling" typed without JSON -> treat as single
      return [trimmed];
    }
    return []; // fallback safe
  }

  // everything else: keep as string (or JSON if you later add object keys)
  return raw;
}

function normalizeIncomingXhvValue(key: string, value: unknown): JsonValue {
  const isArrayKey = key === 'systemTypes' || key === 'excludeSystemTypes';

  if (isArrayKey) {
    if (Array.isArray(value)) {
      return value.filter((entry): entry is string => typeof entry === 'string');
    }

    if (typeof value === 'string') {
      return normalizeXhvValue(key, value);
    }

    return [];
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return value;
  }

  if (Array.isArray(value)) {
    return value as JsonValue;
  }

  if (value && typeof value === 'object') {
    return value as JsonValue;
  }

  return '';
}

function normalizeHvMeta(x?: Record<string, JsonValue>): HvMeta | undefined {
  if (!x || Object.keys(x).length === 0) return undefined;

  const normalized = Object.fromEntries(
    Object.entries(x).map(([key, value]) => [key, normalizeIncomingXhvValue(key, value)]),
  ) as HvMeta;

  return Object.keys(normalized).length ? normalized : undefined;
}
